import React, { useRef, useState, useCallback } from "react";
import NewImageDisplay from "../pages/SnapCamera/NewImageDisplay";
import Compressor from "compressorjs";

const CaptureComponent = ({
  videoRef,
  canvasRef,
  targetElementId,
  modelConfig,
  logoUrl,
  titleHeader,
  config,
  text,
  imagePrompt,
  onImageUpload,
  usePortal = false,
  autoTriggerAPI = true,
  endpoint = "rag",
  onCapture,
  onCaptureComplete,
  onClose,
  onAPIResponse,
  children,
}) => {
  const [hasCapturedImage, setHasCapturedImage] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isAPILoading, setIsAPILoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const internalCanvasRef = useRef(null);
  const abortControllerRef = useRef(null);

  const BASE_URL =
    process.env.REACT_APP_API_HOST_URL?.trim() || "https://devapi.fiduciaai.in";
  const sessionId = sessionStorage.getItem("sessionId") || "default_session_id";

  const getCanvas = useCallback(() => {
    return canvasRef?.current || internalCanvasRef.current;
  }, [canvasRef]);

  const compressFile = useCallback(
    (file) => {
      const isEnabled = Boolean(modelConfig?.compressionEnabled);
      const quality = parseFloat(modelConfig?.compressionQuality);

      if (!isEnabled || isNaN(quality)) {
        console.debug(
          !isEnabled
            ? "Compression flag disabled; returning original file."
            : "No valid compressionQuality set; returning original file."
        );
        return Promise.resolve(file);
      }

      return new Promise((resolve, reject) => {
        console.debug(`Original file size: ${file.size} bytes`);
        new Compressor(file, {
          quality,
          mimeType: "image/png",
          width: file.width,
          height: file.height,
          convertSize: 0,
          strict: true,
          success(resultBlob) {
            console.debug(`After compression (PNG): ${resultBlob.size} bytes`);
            resolve(resultBlob);
          },
          error(err) {
            console.error("Compression error:", err);
            reject(err);
          },
        });
      });
    },
    [modelConfig?.compressionEnabled, modelConfig?.compressionQuality]
  );

  const callImageAPI = useCallback(
    async (imageDataUrl) => {
      if (!autoTriggerAPI || !modelConfig) {
        console.debug(
          "API call skipped - autoTriggerAPI disabled or no modelConfig"
        );
        return;
      }

      setIsAPILoading(true);

      try {
        abortControllerRef.current = new AbortController();

        const formData = new FormData();
        formData.append("customerkey", modelConfig.customerKey);
        formData.append("ocid", modelConfig.ocid);
        formData.append("query", "");
        formData.append(
          "default_query",
          modelConfig.defaultPrompt || "Some default query"
        );
        formData.append("html_flag", "true");
        formData.append("model", modelConfig.model);
        formData.append("threshold", modelConfig.threshold);
        formData.append("context_prompt", modelConfig.context_prompt);
        formData.append("record_size", modelConfig.record_size);
        formData.append("conversation_id", sessionId);
        formData.append("bottype", "cam");
        formData.append(
          "image_query",
          modelConfig.imagePrompt || text || imagePrompt
        );
        formData.append("chain_model", modelConfig.chainModel);
        formData.append("max_tokens", modelConfig.maxTokens);
        formData.append("language", modelConfig.language || "english");
        formData.append(
          "langgraph",
          modelConfig.langGraph?.toString() || "false"
        );

        const res = await fetch(imageDataUrl);
        const blob = await res.blob();
        const file = new File([blob], "captured_frame.png", {
          type: blob.type || "image/png",
        });

        const compressedBlob = await compressFile(file);
        const compressedFile = new File([compressedBlob], file.name, {
          type: compressedBlob.type,
        });
        formData.append("images", compressedFile);

        const response = await fetch(
          BASE_URL + "/" + endpoint + "/api/v2/chat/img/stream/response",
          {
            method: "POST",
            body: formData,
            headers: { Accept: "text/event-stream" },
            signal: abortControllerRef.current.signal,
          }
        );

        if (response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let fullResponse = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const extractedContent = (chunk.match(/\$(.*?)\$/gs) || [])
              .map((str) => str.slice(1, -1))
              .join("");

            if (
              extractedContent &&
              !extractedContent.includes("duration_for_embeddings")
            ) {
              fullResponse += extractedContent;
              setApiResponse(fullResponse);
            }
          }

          if (onAPIResponse) {
            onAPIResponse(fullResponse);
          }
        }

        setIsAPILoading(false);
      } catch (error) {
        console.error("Error calling image API:", error);
        setIsAPILoading(false);
      }
    },
    [
      autoTriggerAPI,
      modelConfig,
      sessionId,
      endpoint,
      text,
      imagePrompt,
      compressFile,
      onAPIResponse,
      BASE_URL,
    ]
  );

  const captureFromVideo = useCallback(
    (videoElement) => {
      if (!videoElement) {
        console.error("Video element not available for capture");
        return null;
      }

      const canvas = getCanvas();
      if (!canvas) {
        console.error("Canvas not available for capture");
        return null;
      }

      const context = canvas.getContext("2d");
      const width = videoElement.videoWidth || videoElement.clientWidth;
      const height = videoElement.videoHeight || videoElement.clientHeight;

      canvas.width = width;
      canvas.height = height;

      if (videoElement.readyState < 2) {
        console.warn("Video not ready for capture");
        return null;
      }

      context.drawImage(videoElement, 0, 0, width, height);
      return canvas.toDataURL("image/png");
    },
    [getCanvas]
  );

  const captureFromElement = useCallback(async (elementId) => {
    try {
      const html2canvas = await import("html2canvas");
      const element = document.getElementById(elementId);

      if (!element) {
        console.error(`Element with id '${elementId}' not found`);
        return null;
      }

      const canvas = await html2canvas.default(element, {
        backgroundColor: null,
        allowTaint: true,
        useCORS: true,
        scale: 1,
      });

      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error capturing element:", error);
      return null;
    }
  }, []);

  const capturePhoto = useCallback(async () => {
    if (onCapture) {
      onCapture();
    }

    let imageDataUrl = null;

    try {
      if (videoRef?.current) {
        imageDataUrl = captureFromVideo(videoRef.current);
      }
      else if (targetElementId) {
        imageDataUrl = await captureFromElement(targetElementId);
      }
      else {
        imageDataUrl = await captureFromElement("root");
      }

      if (imageDataUrl) {
        setCapturedImage(imageDataUrl);
        setHasCapturedImage(true);

        if (autoTriggerAPI) {
          await callImageAPI(imageDataUrl);
        }

        if (onCaptureComplete) {
          onCaptureComplete(imageDataUrl);
        }
      } else {
        console.error("Failed to capture image");
      }
    } catch (error) {
      console.error("Capture error:", error);
    }
  }, [
    videoRef,
    targetElementId,
    captureFromVideo,
    captureFromElement,
    onCapture,
    onCaptureComplete,
    autoTriggerAPI,
    callImageAPI,
  ]);

  const clearCapture = useCallback(() => {
    setHasCapturedImage(false);
    setCapturedImage(null);
    setApiResponse(null);
    setIsAPILoading(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (hasCapturedImage && capturedImage) {
    return (
      <NewImageDisplay
        photo={capturedImage}
        image={capturedImage}
        modelConfig={modelConfig}
        handleClose={clearCapture}
        logoUrl={logoUrl}
        titleHeader={titleHeader}
        config={config}
        text={text}
        imagePrompt={imagePrompt}
        onImageUpload={onImageUpload}
        usePortal={usePortal}
      />
    );
  }

  return (
    <>
      <canvas ref={internalCanvasRef} style={{ display: "none" }} />

      {children &&
        children({
          capturePhoto,
          hasCapturedImage,
          capturedImage,
          clearCapture,
          isAPILoading,
          apiResponse,
        })}
    </>
  );
};

export default CaptureComponent;
